import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Clock, User, Trophy, Radio } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { API } from '@/App';
import { format, parseISO } from 'date-fns';

export default function MatchDetailPage({ user, setUser }) {
  const { matchId } = useParams();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMatch = async () => {
      try {
        const response = await axios.get(`${API}/matches/${matchId}`);
        setMatch(response.data);
      } catch (error) {
        console.error('Error fetching match:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMatch();
    // Poll every 10 seconds for live updates
    const interval = setInterval(fetchMatch, 10000);
    return () => clearInterval(interval);
  }, [matchId]);

  const formatDateTime = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'EEEE, MMMM d, yyyy • h:mm a');
    } catch {
      return dateStr;
    }
  };

  const formatOvers = (overs) => {
    const fullOvers = Math.floor(overs);
    const balls = Math.round((overs - fullOvers) * 10);
    return `${fullOvers}.${balls}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 main-content-mobile">
        <header className="fixed top-0 left-0 right-0 z-40 bg-blue-600 h-14"></header>
        <div className="pt-14 p-4">
          <div className="skeleton h-48 rounded-2xl mb-4"></div>
          <div className="skeleton h-64 rounded-2xl"></div>
        </div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-bold text-slate-900">Match not found</h1>
          <Link to="/leagues" className="text-blue-600 hover:underline mt-2 inline-block">Back to Leagues</Link>
        </div>
      </div>
    );
  }

  const currentInnings = match.innings?.find(i => !i.is_completed) || match.innings?.[match.innings.length - 1];
  const isLive = match.status === 'live';

  return (
    <div className="min-h-screen bg-slate-50 main-content-mobile">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-40 ${isLive ? 'bg-red-600' : 'bg-blue-600'} text-white`}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-3 h-14">
            <Link to={match.league ? `/leagues/${match.league.league_id}` : '/leagues'}>
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                {isLive && <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>}
                <span className="font-semibold">{isLive ? 'LIVE' : match.status.toUpperCase()}</span>
              </div>
            </div>
            <span className="text-sm opacity-80">{match.overs} overs</span>
          </div>
        </div>
      </header>

      {/* Score Display */}
      <div className={`pt-14 ${isLive ? 'bg-red-600' : 'bg-blue-600'} text-white pb-8`}>
        <div className="max-w-4xl mx-auto px-4">
          {/* Teams */}
          <div className="flex items-center justify-center gap-6 py-8">
            <Link to={`/teams/${match.team_a?.team_id}`} className="text-center">
              {match.team_a?.logo_url ? (
                <img src={match.team_a.logo_url} alt={match.team_a.name} className="w-16 h-16 rounded-xl mx-auto" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center mx-auto text-2xl font-bold">
                  {match.team_a?.short_name?.charAt(0) || match.team_a?.name?.charAt(0)}
                </div>
              )}
              <p className="mt-2 font-semibold">{match.team_a?.short_name || match.team_a?.name}</p>
              {match.innings?.[0] && match.innings[0].batting_team_id === match.team_a_id && (
                <p className="font-stats text-2xl mt-1">{match.innings[0].total_runs}/{match.innings[0].total_wickets}</p>
              )}
              {match.innings?.[1] && match.innings[1].batting_team_id === match.team_a_id && (
                <p className="font-stats text-2xl mt-1">{match.innings[1].total_runs}/{match.innings[1].total_wickets}</p>
              )}
            </Link>

            <div className="text-center">
              <p className="text-3xl font-bold opacity-50">VS</p>
            </div>

            <Link to={`/teams/${match.team_b?.team_id}`} className="text-center">
              {match.team_b?.logo_url ? (
                <img src={match.team_b.logo_url} alt={match.team_b.name} className="w-16 h-16 rounded-xl mx-auto" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center mx-auto text-2xl font-bold">
                  {match.team_b?.short_name?.charAt(0) || match.team_b?.name?.charAt(0)}
                </div>
              )}
              <p className="mt-2 font-semibold">{match.team_b?.short_name || match.team_b?.name}</p>
              {match.innings?.[0] && match.innings[0].batting_team_id === match.team_b_id && (
                <p className="font-stats text-2xl mt-1">{match.innings[0].total_runs}/{match.innings[0].total_wickets}</p>
              )}
              {match.innings?.[1] && match.innings[1].batting_team_id === match.team_b_id && (
                <p className="font-stats text-2xl mt-1">{match.innings[1].total_runs}/{match.innings[1].total_wickets}</p>
              )}
            </Link>
          </div>

          {/* Current Score */}
          {currentInnings && isLive && (
            <div className="text-center">
              <p className="text-sm opacity-70">Overs: {formatOvers(currentInnings.total_overs)}</p>
              {currentInnings.target && (
                <p className="mt-2 text-orange-300">
                  Need {currentInnings.target - currentInnings.total_runs} from {(match.overs * 6) - currentInnings.total_balls} balls
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Match Info */}
      <div className="px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-4">
          <Card className="card-base">
            <CardContent className="p-4">
              <h3 className="font-semibold text-slate-900 mb-4">Match Details</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-slate-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Date & Time</p>
                    <p className="font-medium text-slate-900">{formatDateTime(match.match_date_time)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-slate-500" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Venue</p>
                    <p className="font-medium text-slate-900">{match.ground}</p>
                  </div>
                </div>
                {match.league && (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                      <Trophy className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">League</p>
                      <Link to={`/leagues/${match.league.league_id}`} className="font-medium text-blue-600 hover:underline">
                        {match.league.name}
                      </Link>
                    </div>
                  </div>
                )}
                {match.toss_winner_id && (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-500" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Toss</p>
                      <p className="font-medium text-slate-900">
                        {match.toss_winner_id === match.team_a_id ? match.team_a?.name : match.team_b?.name} won, elected to {match.toss_decision}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Innings Summary */}
          {match.innings?.length > 0 && (
            <Card className="card-base">
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-900 mb-4">Innings</h3>
                <div className="space-y-4">
                  {match.innings.map((inn, idx) => {
                    const battingTeam = inn.batting_team_id === match.team_a_id ? match.team_a : match.team_b;
                    return (
                      <div key={inn.innings_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <div>
                          <p className="font-medium">{battingTeam?.name}</p>
                          <p className="text-sm text-slate-500">{formatOvers(inn.total_overs)} overs</p>
                        </div>
                        <div className="text-right">
                          <p className="font-stats text-2xl">{inn.total_runs}/{inn.total_wickets}</p>
                          {inn.target && <p className="text-sm text-slate-500">Target: {inn.target}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Placeholder for non-started matches */}
          {match.status === 'scheduled' && (
            <Card className="card-base">
              <CardContent className="p-8 text-center">
                <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Match hasn't started yet</p>
                <p className="text-sm text-slate-400 mt-1">Live scores will appear here once the match begins</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
